package br.com.htms.inspeccaocampo.data

/**
 * Une rótulos equivalentes de obra e responsável nos dashboards e no Excel consolidado.
 * Não altera o cadastro gravado — só a agregação na leitura.
 */
object AgregacaoInspecao {

    /** Aliases explícitos → nome canônico do catálogo (com CC). */
    private val ALIASES_OBRA: Map<String, String> = mapOf(
        chave("SE SALTO SANTIAGO - ENERGIA") to "3009 - SE SALTO SANTIAGO - ENERGIA",
        chave("SESALTO SANTIAGO - ENERGIA") to "3009 - SE SALTO SANTIAGO - ENERGIA",
        chave("SE CURITIBA - AXIA - ENERGIA") to "3011 - SE CURITIBA - AMP. N",
        chave("AXIA - Subestação Passo Fundo - SE PFU") to "3016 - AXIA - Subestação Passo Fundo - SE PFU",
        chave("AXIA - Subestação Passo Fundo") to "3016 - AXIA - Subestação Passo Fundo - SE PFU",
        chave("SE CASCAVEL NORTE") to "3014 - SE CASCAVEL NORTE (3º Autotransformador 230/138 kV de 150 MVA)"
    )

    fun chave(raw: String): String =
        raw.trim()
            .uppercase()
            .replace('Á', 'A').replace('À', 'A').replace('Ã', 'A').replace('Â', 'A')
            .replace('É', 'E').replace('Ê', 'E')
            .replace('Í', 'I')
            .replace('Ó', 'O').replace('Ô', 'O').replace('Õ', 'O')
            .replace('Ú', 'U').replace('Ü', 'U')
            .replace('Ç', 'C')
            .replace(Regex("\\s+"), " ")
            .replace(Regex("\\s*-\\s*"), " - ")
            .trim()

    fun obra(raw: String?, vazio: String = "Obra não informada"): String {
        val t = raw?.trim().orEmpty()
        if (t.isBlank()) return vazio
        val k = chave(t)
        ALIASES_OBRA[k]?.let { return it }
        ObraCatalog.SEED_NOMES.find { chave(it) == k }?.let { return it }
        val semCodigo = k.replace(Regex("^\\d{4} - "), "")
        ObraCatalog.SEED_NOMES.forEach { catalog ->
            val catSem = chave(catalog).replace(Regex("^\\d{4} - "), "")
            if (catSem == k || catSem == semCodigo) return catalog
        }
        val codigo = Regex("^(\\d{4})\\s*-").find(t)?.groupValues?.get(1)
        if (codigo != null) {
            ObraCatalog.SEED_NOMES.find { it.startsWith("$codigo -") }?.let { return it }
        }
        return t
    }

    /** Username curto (ex.: antonia.nascimento) vs nome completo. */
    fun pareceLogin(raw: String): Boolean {
        val s = raw.trim()
        if (s.isBlank() || s.contains(' ')) return false
        return s.contains('.') || s.contains('_') || s.all { it.isLowerCase() || it.isDigit() || it == '.' || it == '_' }
    }

    private val STOP_NOME = setOf("das", "dos", "del", "van", "von")

    private fun tokensOrdenados(raw: String): List<String> {
        val base = if (pareceLogin(raw)) {
            raw.lowercase().replace('_', '.').split('.', '-', ' ')
        } else {
            chave(raw).lowercase().split(' ', '-', '.')
        }
        return base.map { it.trim() }.filter { it.length >= 3 && it !in STOP_NOME }
    }

    private fun tokens(raw: String): Set<String> = tokensOrdenados(raw).toSet()

    /** Nome curto (ex.: Antonia Nascimento) cabe no completo (mesmo 1º e último token). */
    private fun nomeCurtoDoCompleto(curto: String, longo: String): Boolean {
        val a = tokensOrdenados(curto)
        val b = tokensOrdenados(longo)
        if (a.size < 2 || a.size >= b.size) return false
        fun bate(x: String, y: String) = x == y || x.startsWith(y) || y.startsWith(x)
        if (!b.any { bate(it, a.first()) }) return false
        if (!b.any { bate(it, a.last()) }) return false
        return a.all { t -> b.any { bate(it, t) } }
    }

    /**
     * Mapa rótulo bruto → rótulo canônico (prefere nome completo).
     * Une capitalização, login (parte.sobrenome) e nome abreviado com o nome por extenso.
     */
    fun mapaResponsaveis(rotulos: Collection<String>): Map<String, String> {
        val limpos = rotulos.map { it.trim() }.filter { it.isNotBlank() }.distinct()
        if (limpos.isEmpty()) return emptyMap()

        val porChave = linkedMapOf<String, MutableList<String>>()
        limpos.forEach { r ->
            porChave.getOrPut(chave(r)) { mutableListOf() }.add(r)
        }

        fun preferido(grupo: List<String>): String =
            grupo.maxWithOrNull(
                compareBy<String> { if (pareceLogin(it)) 0 else 1 }
                    .thenBy { it.count { c -> c == ' ' } }
                    .thenBy { it.length }
            ) ?: grupo.first()

        val canonPorChave = porChave.mapValues { (_, v) -> preferido(v) }.toMutableMap()
        val nomes = canonPorChave.values.filterNot { pareceLogin(it) }.distinct()
        val logins = canonPorChave.values.filter { pareceLogin(it) }

        for (login in logins) {
            val tok = tokens(login)
            if (tok.isEmpty()) continue
            val alvo = nomes.firstOrNull { nome ->
                val nt = tokens(nome)
                tok.all { t -> nt.any { n -> n.startsWith(t) || t.startsWith(n) } }
            } ?: continue
            canonPorChave[chave(login)] = alvo
        }

        // "Antonia Nascimento" → "ANTONIA TEREZINHA DE LIMA DO NASCIMENTO"
        val nomesAposLogin = canonPorChave.values.filterNot { pareceLogin(it) }.distinct()
            .sortedWith(compareByDescending<String> { tokens(it).size }.thenByDescending { it.length })
        for (curto in nomesAposLogin) {
            val completo = nomesAposLogin.firstOrNull { longo ->
                longo != curto && nomeCurtoDoCompleto(curto, longo)
            } ?: continue
            canonPorChave[chave(curto)] = completo
        }
        // Reaplica: quem apontava ao curto passa a apontar ao completo
        canonPorChave.keys.toList().forEach { k ->
            val v = canonPorChave[k] ?: return@forEach
            val v2 = canonPorChave[chave(v)]
            if (v2 != null && v2 != v) canonPorChave[k] = v2
        }

        return limpos.associateWith { r -> canonPorChave[chave(r)] ?: r }
    }

    fun responsavel(raw: String?, mapa: Map<String, String>, vazio: String = "Não informado"): String {
        val t = raw?.trim().orEmpty()
        if (t.isBlank()) return vazio
        return mapa[t] ?: mapa.entries.firstOrNull { chave(it.key) == chave(t) }?.value ?: t
    }

    fun diaSemana(iso: String): String {
        val dias = br.com.htms.inspeccaocampo.data.colaboradores.DatasColab.diasDeIso(iso) ?: return "—"
        // 1970-01-01 = quinta; 0 = domingo
        val idx = ((dias % 7) + 4).toInt() % 7
        return listOf("Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado")[idx]
    }
}
